/**
 * Siege Lab runtime — one brick wall that is simultaneously a box3d rigid-body
 * structure, a src/damage ledger, and the anchor for src/particles bursts.
 *
 * Dependency direction: this module composes the standalone systems (physics →
 * damage is allowed; src/damage itself still imports nothing from physics).
 * One ball→brick contact becomes ONE event that fans out to the ledger stamp,
 * the fracture coupling (integrity-scaled detachment), and — via the pending
 * queue the page drains — a particle burst with the same shape.
 */
import {
  BodyPool,
  SleepManager,
  FixedStepper,
  TransformBuffer,
  createBox3D,
  probeCapabilities,
  type BodyHandle,
  type ShapeHandle,
  type Box3D,
  type World,
} from 'box3d-web';
import { clampEnergy } from '../../services/energy'; // shared energy slider; canonical module is services/energy.ts
import { hash01 } from './damage/hash';
import { BRICK_PROFILE } from './damage/profiles';
import { buildResidue, emptyResidue, type ResidueStamp, type ResidueState } from './damage/residue';
import { createDamageLedger, type DamageLedger, type StampResult } from './damage/ledger';
import { buildWallGrid, resolveWallSpec, type WallGrid } from './damage/wallGrid';
import type { LastStampCall } from './damage/types';
import { shrinkHullPoints } from '../../services/fracture/cellGeometry';
import { fractureBox, radialBiasedBrickSeeds, type FractureCell, type Vec3 } from '../../services/fracture/voronoiFracture';
import {
  applyComminution,
  energyDensityFromEvent,
  MIN_VISIBLE_CHUNK_FRACTION,
} from '../../services/comminution';

export const SIEGE_FIXED_DT = 1 / 60;
export const SIEGE_SUBSTEPS = 4;

/** Running-bond wall: 20 × 18 courses of real-proportion bricks (~7.8 × 2.7 m). */
export const SIEGE_COLS = 20;
export const SIEGE_ROWS = 18;
export const SIEGE_BRICK = { length: 0.42, height: 0.14, depth: 0.2 };
export const SIEGE_JOINT = 0.012;
export const SIEGE_SEED = 7;

export const SIEGE_WALL_SPEC = {
  width: SIEGE_COLS * (SIEGE_BRICK.length + SIEGE_JOINT),
  height: SIEGE_ROWS * (SIEGE_BRICK.height + SIEGE_JOINT),
  brickLength: SIEGE_BRICK.length,
  brickHeight: SIEGE_BRICK.height,
  brickDepth: SIEGE_BRICK.depth,
  joint: SIEGE_JOINT,
  seed: SIEGE_SEED,
};

/** Bodies are built a hair under grid size so joints read as real gaps. */
const FIT_X = 0.005;
const FIT_Y = 0.004;
const FIT_Z = 0.005;

const BRICK_DENSITY = 4;
const BRICK_FRICTION = 0.82;
const BRICK_RESTITUTION = 0.03;

export const BALL_RADIUS = 0.26;
export const BALL_DENSITY = 55;
export const MAX_BALLS = 6;

const MIN_IMPACT_SPEED = 4;
const GROUND_PUFF_SPEED = 5;

/** Rim-zone bricks below this integrity crumble out on the next hit. */
export const CRUMBLE_INTEGRITY = 0.55;
const BASE_DETACH_IMPULSE = 0.34;
const DETACH_UP_BIAS = 0.38;

const RETIRE_DWELL_SEC = 1.25;
const RETIRE_SPEED_EPS = 0.08;
const CORRIDOR_RADIUS = 1.05;
const OOB_CULL_Y = -2;
/** Visible yard. Shards yeeted past this project onto the horizon as sky specks. */
const YARD_MAX_X = 14;
const YARD_MIN_Z = -7;
const YARD_MAX_Z = 16;

/** Live fragment-body cap. Oldest settled shard is evicted first; never stall. */
export const MAX_FRAGMENTS = 96;
/**
 * Below this lab-energy, every detached brick flies whole. A full-charge
 * contact (~2.0–2.9) is well above; a low-charge tap (~0.5–0.8) is not.
 */
export const FRACTURE_ENERGY_MIN = 1.35;
const FRACTURE_BUDGET_MID = 1.75;
const FRACTURE_BUDGET_HIGH = 2.25;
const FRAGMENT_HULL_SHRINK = 0.88;
const FRAGMENT_MAX_ASPECT = 6.5;
const FRAGMENT_SIZE_FLOOR_FRAC = 0.1;
const FRAGMENT_MAX_AGE_SEC = 90;
const HIDDEN_BRICK_Y = -80;
const COMMINUTION_PARAMS = { coefficient: 0.35, pulverizeSizeThreshold: 0.0024 } as const;

export interface SiegeCaps {
  awakeBodyCount: boolean;
  bodyCount: boolean;
  setBodyType: boolean;
  getLinearVelocity: boolean;
  setAwake: boolean;
  readSleepStates: boolean;
  convexHull: boolean;
  setBodyTransform: boolean;
  angularVelocity: boolean;
  bodyQueries: boolean;
}

export interface SiegeEvent {
  /** Wall-local stamp coords (x centered, y from ground). */
  lx: number;
  ly: number;
  /** World contact point + shot direction. */
  wx: number;
  wy: number;
  wz: number;
  dx: number;
  dy: number;
  dz: number;
  energy: number;
  speed: number;
}

export interface SiegeFanout {
  kind: 'wall' | 'ground';
  event: SiegeEvent;
  stamp: StampResult | null;
  lastCall: LastStampCall | null;
  detached: number[];
  /** Subset of `detached` that swapped into hull fragments this event. */
  fractured: number[];
  /** Pulverized volume per `detached` index (0 for whole-brick). */
  pulverizedVolume: number[];
}

export interface SiegeContactBatch {
  readonly begins: readonly { bodyA: BodyHandle; bodyB: BodyHandle; approachSpeed: number }[];
  readonly ends: readonly unknown[];
  readonly hits: readonly unknown[];
}

export interface SiegeFragment {
  body: BodyHandle;
  cell: FractureCell;
  volume: number;
  brickIndex: number;
  seed: number;
  settled: boolean;
  bornAt: number;
  dwell: number;
}

export interface BrickFracturePlan {
  seedCount: number;
  cells: FractureCell[];
  keep: FractureCell[];
  pulverize: FractureCell[];
  keepVolume: number;
  pulverizedVolume: number;
  brickVolume: number;
}

export interface BrickFractureOpts {
  /** Closest 1–2 contact bricks: 5–8 seeds instead of 3–6. */
  coreBoost?: boolean;
}

export interface SiegeRuntime {
  world: World;
  caps: SiegeCaps;
  stepper: FixedStepper;
  buffer: TransformBuffer;
  sleep: SleepManager;
  grid: WallGrid;
  ledger: DamageLedger;
  residue: ResidueState;
  stamps: ResidueStamp[];
  brickBodies: BodyHandle[];
  cannonballs: BodyHandle[];
  detached: Uint8Array;
  retired: Uint8Array;
  retireDwell: Float32Array;
  detachedCount: number;
  retiredCount: number;
  awakeCount: number;
  awakeBrickCount: number;
  sleepStates: Uint8Array;
  brickBodyIds: Int32Array;
  stepMs: number;
  simTime: number;
  slowMo: boolean;
  charge: number;
  pending: SiegeFanout[];
  lastCall: LastStampCall | null;
  bodyIndex: Map<number, number>;
  idsDirty: boolean;
  prevValid: boolean;
  prevTransforms: Float32Array;
  staticCount: number;
  fractured: Uint8Array;
  /** Last in-world brick pose (frozen at fracture so i*7 stays coherent). */
  brickPose: Float32Array;
  fragmentBuffer: TransformBuffer;
  fragmentPool: BodyPool;
  fragments: Map<number, SiegeFragment>;
  fragmentSleep: SleepManager;
  fragmentWatchDirty: boolean;
  fragmentCount: number;
  fragmentsSpawnedTotal: number;
  awakeFragmentCount: number;
  fragmentSleepStates: Uint8Array;
  fragmentBodyIds: Int32Array;
  fragmentGen: number;
  nextHangCheckSim: number;
  ownership?: SiegeNativeOwnership;
  hosted: boolean;
  cannonballOrdinal: number;
  nextFragmentOrdinal: number;
  groundBody: BodyHandle;
}

export type SiegeNativeParticipant =
  | { readonly kind: 'brick'; readonly ordinal: number }
  | { readonly kind: 'fragment'; readonly ordinal: number; readonly sourceBrickOrdinal: number }
  | { readonly kind: 'projectile'; readonly ordinal: number };

export interface SiegeNativeOwnership {
  registerBody(body: BodyHandle, participant: SiegeNativeParticipant): void;
  registerShape(shape: ShapeHandle, body: BodyHandle, participant: SiegeNativeParticipant): void;
  unregisterBody(body: BodyHandle): void;
}

export interface HostedSiegeRuntimeOptions {
  readonly world: World;
  readonly groundBody: BodyHandle;
  readonly ownership: SiegeNativeOwnership;
}

let modulePromise: Promise<Box3D> | null = null;
let runtime: SiegeRuntime | null = null;
let runtimePromise: Promise<SiegeRuntime> | null = null;
/** Sim-time of the next hanging-intact-brick support scan (2 s cadence). */

function loadModule() {
  if (!modulePromise) modulePromise = createBox3D();
  return modulePromise;
}

export function getExistingSiegeRuntime() {
  return runtime;
}

/** Ball muzzle speed for a 0..1 charge. */
export function muzzleSpeed(charge: number): number {
  const c = Number.isFinite(charge) ? Math.min(1, Math.max(0, charge)) : 0.75;
  return 34 + 30 * c;
}

/** Lab-energy for a ball hitting the wall at `speed` with `charge`. */
export function impactEnergy(speed: number, charge: number): number {
  const s = Number.isFinite(speed) ? Math.max(0, speed) : 0;
  return clampEnergy(1.9 * (s / 52) ** 2 * (0.55 + 0.45 * (Number.isFinite(charge) ? charge : 0.75)));
}

/** Contact approachSpeed can clip 10–20% of muzzle; keep full-charge in the high band. */
export function siegeHitEnergy(approachSpeed: number, charge: number): number {
  const contact = impactEnergy(approachSpeed, charge);
  const nominal = impactEnergy(muzzleSpeed(charge), charge);
  return Math.max(contact, nominal * 0.88);
}

/** Detachment ease factor: fresh bricks resist, damaged bricks let go. */
export function detachImpulseFactor(integrity: number): number {
  const t = Number.isFinite(integrity) ? Math.min(1, Math.max(0, integrity)) : 1;
  return 1.15 - 0.75 * t;
}

/** Grid↔body bijection guard: the resolved spec must produce exactly cols×rows. */
export function resolveSiegeWall(): ReturnType<typeof resolveWallSpec> {
  const spec = resolveWallSpec(SIEGE_WALL_SPEC);
  if (spec.nCols !== SIEGE_COLS || spec.nRows !== SIEGE_ROWS) {
    throw new Error(`siege wall spec mismatch: ${spec.nCols}x${spec.nRows}`);
  }
  return spec;
}

/** Visual brick half-extents (fracture cells match the rendered box). */
export function siegeBrickHalf(): Vec3 {
  return {
    x: SIEGE_BRICK.length * 0.5,
    y: SIEGE_BRICK.height * 0.5,
    z: SIEGE_BRICK.depth * 0.5,
  };
}

/** How many core bricks may fracture on this event (0 = whole-brick only). */
export function fractureBudget(energy: number): number {
  if (!(energy >= FRACTURE_ENERGY_MIN)) return 0;
  if (energy < FRACTURE_BUDGET_MID) return 1;
  if (energy < FRACTURE_BUDGET_HIGH) return 3;
  return 3;
}

/** True when live + need fits, or oldest settled shards can be evicted to fit. */
export function canSpawnFragments(liveCount: number, need: number, settledLive: number): boolean {
  if (!(need > 0)) return false;
  const live = Math.max(0, liveCount | 0);
  const extra = Math.max(0, need | 0);
  const settled = Math.max(0, settledLive | 0);
  if (live + extra <= MAX_FRAGMENTS) return true;
  return live - settled + extra <= MAX_FRAGMENTS;
}

function hash01Rng(seed: number): () => number {
  let i = 0;
  return () => hash01(seed, ++i);
}

/** Voronoi seeds inside the brick, radial-biased from a hashed impact. */
export function brickFragmentSeeds(half: Vec3, brickSeed: number, count: number): Vec3[] {
  return radialBiasedBrickSeeds(half, count, hash01Rng(brickSeed));
}

function cellAspect(cell: FractureCell): number {
  const dx = cell.aabb.max.x - cell.aabb.min.x;
  const dy = cell.aabb.max.y - cell.aabb.min.y;
  const dz = cell.aabb.max.z - cell.aabb.min.z;
  const longest = Math.max(dx, dy, dz);
  const shortest = Math.max(1e-6, Math.min(dx, dy, dz));
  return longest / shortest;
}

/**
 * Deterministic per-brick split: Voronoi 3–6 cells (5–7 on the contact core),
 * then comminution. Same seed + energy + band → same keep/pulverize partition.
 */
export function planBrickFracture(
  brickSeed: number,
  halfExtents: Vec3,
  energy: number,
  opts?: BrickFractureOpts,
): BrickFracturePlan {
  const seedCount = opts?.coreBoost
    ? 5 + Math.floor(hash01(brickSeed, 97) * 3)
    : 3 + Math.floor(hash01(brickSeed, 97) * 4);
  const seeds = brickFragmentSeeds(halfExtents, brickSeed, seedCount);
  const cells = fractureBox(halfExtents, seeds);
  const brickVolume = 8 * halfExtents.x * halfExtents.y * halfExtents.z;
  const sizeFloor = Math.max(0.0007, brickVolume * FRAGMENT_SIZE_FLOOR_FRAC);
  const coreRadius = Math.max(0.05, Math.hypot(halfExtents.x, halfExtents.y, halfExtents.z) * 0.45);
  const classified = applyComminution({
    pieces: cells.map((cell) => ({
      volume: cell.volume,
      dist: Math.hypot(cell.centroid.x, cell.centroid.y, cell.centroid.z),
    })),
    params: COMMINUTION_PARAMS,
    energyDensity: energyDensityFromEvent(Math.max(0, energy), coreRadius),
    coreRadius,
    sizeFloorVol: sizeFloor,
    minChunkFraction: MIN_VISIBLE_CHUNK_FRACTION,
  });
  const pulverizeSet = new Set(classified.pulverizeIndices);
  const keep: FractureCell[] = [];
  const pulverize: FractureCell[] = [];
  let keepVolume = 0;
  let pulverizedVolume = 0;
  for (let i = 0; i < cells.length; i += 1) {
    const cell = cells[i];
    const needle = cellAspect(cell) > FRAGMENT_MAX_ASPECT || cell.volume < sizeFloor;
    if (pulverizeSet.has(i) || needle) {
      pulverize.push(cell);
      pulverizedVolume += cell.volume;
    } else {
      keep.push(cell);
      keepVolume += cell.volume;
    }
  }
  return {
    seedCount,
    cells,
    keep,
    pulverize,
    keepVolume,
    pulverizedVolume,
    brickVolume,
  };
}

function rotateByQuat(
  x: number,
  y: number,
  z: number,
  qx: number,
  qy: number,
  qz: number,
  qw: number,
): Vec3 {
  const ix = qw * x + qy * z - qz * y;
  const iy = qw * y + qz * x - qx * z;
  const iz = qw * z + qx * y - qy * x;
  const iw = -qx * x - qy * y - qz * z;
  return {
    x: ix * qw + iw * -qx + iy * -qz - iz * -qy,
    y: iy * qw + iw * -qy + iz * -qx - ix * -qz,
    z: iz * qw + iw * -qz + ix * -qy - iy * -qx,
  };
}

function makeCaps(world: World): SiegeCaps {
  const probed = probeCapabilities(world);
  return {
    awakeBodyCount: probed.awakeBodyCount,
    bodyCount: probed.bodyCount,
    setBodyType: probed.setBodyType,
    getLinearVelocity: probed.getLinearVelocity,
    setAwake: probed.setAwake,
    readSleepStates: typeof world.readSleepStates === 'function',
    convexHull: probed.convexHull === true,
    setBodyTransform: probed.setBodyTransform === true,
    angularVelocity: probed.angularVelocity === true,
    bodyQueries: probed.bodyQueries === true,
  };
}

function buildWall(rt: SiegeRuntime): void {
  const { grid } = rt;
  const spec = grid.spec;
  const created: BodyHandle[] = [];
  try {
    rt.brickBodies = grid.bricks.map((place, i) => {
      const body = rt.world.createBody({
        type: 'dynamic',
        position: [place.x, place.y, place.z],
        rotation: [0, 0, 0, 1],
      });
      created.push(body);
      if (rt.ownership) rt.ownership.registerBody(body, { kind: 'brick', ordinal: i });
      const shape = rt.world.addBox(
        body,
        [
          spec.brickLength * 0.5 - FIT_X,
          spec.brickHeight * 0.5 - FIT_Y,
          spec.brickDepth * 0.5 - FIT_Z,
        ],
        { density: BRICK_DENSITY, friction: BRICK_FRICTION, restitution: BRICK_RESTITUTION },
      );
      if (rt.ownership) rt.ownership.registerShape(shape, body, { kind: 'brick', ordinal: i });
      rt.buffer.add(body);
      return body;
    });
  } catch (error) {
    for (const body of created) {
      try { rt.ownership?.unregisterBody(body); } catch { /* continue rollback */ }
      try { rt.world.destroyBody(body); } catch { /* continue rollback */ }
      rt.buffer.remove(body);
    }
    rt.brickBodies = [];
    throw error;
  }
  rt.bodyIndex.clear();
  rt.brickBodies.forEach((body, i) => {
    rt.bodyIndex.set(body, i);
    rt.brickBodyIds[i] = body;
    const place = grid.bricks[i];
    const o = i * 7;
    rt.brickPose[o] = place.x;
    rt.brickPose[o + 1] = place.y;
    rt.brickPose[o + 2] = place.z;
    rt.brickPose[o + 3] = 0;
    rt.brickPose[o + 4] = 0;
    rt.brickPose[o + 5] = 0;
    rt.brickPose[o + 6] = 1;
  });

  // Settle contacts for two fixed steps, then force-sleep so the page idles at
  // awake≈0 immediately (yard discipline).
  if (!rt.hosted) {
    rt.world.step(SIEGE_FIXED_DT, SIEGE_SUBSTEPS);
    rt.world.step(SIEGE_FIXED_DT, SIEGE_SUBSTEPS);
  }
  for (const body of rt.brickBodies) rt.world.sleepBody(body);
  rt.sleep.watch(rt.brickBodies as BodyHandle[], rt.buffer);
  rt.idsDirty = true;
}

async function createSiegeRuntime(hosted?: HostedSiegeRuntimeOptions): Promise<SiegeRuntime> {
  const box3d = hosted ? null : await loadModule();
  const world = hosted?.world ?? box3d!.createWorld({ gravity: [0, -9.8, 0] });
  const grid = buildWallGrid(SIEGE_WALL_SPEC);
  resolveSiegeWall();
  const ledger = createDamageLedger(grid);
  const rt: SiegeRuntime = {
    world,
    caps: makeCaps(world),
    stepper: new FixedStepper({ fixedDt: SIEGE_FIXED_DT, substeps: SIEGE_SUBSTEPS, maxStepsPerFrame: 3 }),
    buffer: new TransformBuffer(grid.bricks.length + MAX_BALLS),
    sleep: new SleepManager(world, {
      sweepIntervalSec: 2,
      moveThreshold: 0.01,
      angleThreshold: 0.002,
      neighborRadius: 2.5,
      shouldSleep: (body) => {
        const i = rt.bodyIndex.get(body as number);
        if (i == null) return true;
        if (rt.fractured[i] || rt.retired[i] || !rt.detached[i]) return true;
        return poseIsSupported(rt, brickCenter(rt, i), rt.brickBodies[i]);
      },
    }),
    grid,
    ledger,
    residue: emptyResidue(grid.bricks.length),
    stamps: [],
    brickBodies: [],
    cannonballs: [],
    detached: new Uint8Array(grid.bricks.length),
    retired: new Uint8Array(grid.bricks.length),
    retireDwell: new Float32Array(grid.bricks.length),
    detachedCount: 0,
    retiredCount: 0,
    awakeCount: 0,
    awakeBrickCount: 0,
    sleepStates: new Uint8Array(grid.bricks.length),
    brickBodyIds: new Int32Array(grid.bricks.length),
    stepMs: 0,
    simTime: 0,
    slowMo: false,
    charge: 0.75,
    pending: [],
    lastCall: null,
    bodyIndex: new Map(),
    idsDirty: true,
    prevValid: false,
    prevTransforms: new Float32Array(grid.bricks.length * 7),
    staticCount: 0,
    fractured: new Uint8Array(grid.bricks.length),
    brickPose: new Float32Array(grid.bricks.length * 7),
    fragmentBuffer: new TransformBuffer(MAX_FRAGMENTS),
    fragmentPool: null as unknown as BodyPool,
    fragments: new Map(),
    fragmentSleep: new SleepManager(world, {
      settleSteps: 1_000_000,
      sweepIntervalSec: 2,
      moveThreshold: 0.01,
      angleThreshold: 0.002,
      neighborRadius: 2.5,
      shouldSleep: (body) => fragmentIsSupported(rt, body),
    }),
    fragmentWatchDirty: true,
    fragmentCount: 0,
    fragmentsSpawnedTotal: 0,
    awakeFragmentCount: 0,
    fragmentSleepStates: new Uint8Array(MAX_FRAGMENTS),
    fragmentBodyIds: new Int32Array(MAX_FRAGMENTS),
    fragmentGen: 0,
    nextHangCheckSim: 0,
    ownership: hosted?.ownership,
    hosted: hosted !== undefined,
    cannonballOrdinal: 0,
    nextFragmentOrdinal: 0,
    groundBody: 0 as BodyHandle,
  };
  rt.fragmentPool = new BodyPool(world, {
    max: MAX_FRAGMENTS,
    maxAgeSec: FRAGMENT_MAX_AGE_SEC,
    onEvict: (body) => evictFragment(rt, body),
  });

  // Ground slab: top face at y=0.
  const ground = hosted?.groundBody ?? world.createBody({ type: 'static', position: [0, -0.6, 0] });
  rt.groundBody = ground;
  if (!hosted) world.addBox(ground, [60, 0.6, 60], { density: 0, friction: 0.9, restitution: 0.02 });
  rt.staticCount += 1;

  buildWall(rt);
  return rt;
}

export async function getSiegeRuntime(): Promise<SiegeRuntime> {
  if (runtime) return runtime;
  if (!runtimePromise) {
    runtimePromise = createSiegeRuntime().then((created) => {
      runtime = created;
      return created;
    });
    runtimePromise.catch(() => {
      runtimePromise = null;
    });
  }
  return runtimePromise;
}

/** Construct Siege in a caller-owned shared world. */
export async function createHostedSiegeRuntime(options: HostedSiegeRuntimeOptions): Promise<SiegeRuntime> {
  return createSiegeRuntime(options);
}

/** Fires a cannonball from `origin` toward `dir` (normalized internally). */
export function spawnCannonball(
  rt: SiegeRuntime,
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
  charge: number,
): void {
  const len = Math.hypot(dir.x, dir.y, dir.z) || 1;
  const nx = dir.x / len;
  const ny = dir.y / len;
  const nz = dir.z / len;
  wakeCorridor(rt, origin, { x: nx, y: ny, z: nz });

  const body = rt.world.createBody({
    type: 'dynamic',
    position: [origin.x, origin.y, origin.z],
    ccd: true,
    linearDamping: 0,
    angularDamping: 0.02,
  });
  const ordinal = rt.cannonballOrdinal++;
  let registered = false;
  try {
    if (rt.ownership) { rt.ownership.registerBody(body, { kind: 'projectile', ordinal }); registered = true; }
    const shape = rt.world.addSphere(body, BALL_RADIUS, {
      density: BALL_DENSITY,
      friction: 0.55,
      restitution: 0.02,
    });
    if (rt.ownership) rt.ownership.registerShape(shape, body, { kind: 'projectile', ordinal });
  } catch (error) {
    if (registered) rt.ownership?.unregisterBody(body);
    rt.world.destroyBody(body);
    throw error;
  }
  const speed = muzzleSpeed(charge);
  rt.world.setLinearVelocity(body, [nx * speed, ny * speed, nz * speed]);
  rt.cannonballs.push(body);
  rt.buffer.add(body);
  rt.idsDirty = true;
  while (rt.cannonballs.length > MAX_BALLS) {
    const oldest = rt.cannonballs.shift();
    if (oldest != null) {
      rt.world.destroyBody(oldest as BodyHandle);
      rt.ownership?.unregisterBody(oldest as BodyHandle);
      rt.buffer.remove(oldest as BodyHandle);
      rt.idsDirty = true;
    }
  }
}

/** Wake retired debris along the ballistic corridor so shots never pass through sleeping rubble. */
function wakeCorridor(
  rt: SiegeRuntime,
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
): void {
  if (!rt.caps.setAwake) return;
  for (let t = 0; t <= 40; t += 0.5) {
    const px = origin.x + dir.x * t;
    const py = origin.y + dir.y * t;
    const pz = origin.z + dir.z * t;
    for (let i = 0; i < rt.brickBodies.length; i += 1) {
      if (!rt.retired[i] || rt.fractured[i]) continue;
      const o = i * 7;
      const bx = rt.brickPose[o];
      const by = rt.brickPose[o + 1];
      const bz = rt.brickPose[o + 2];
      const dx = bx - px;
      const dy = by - py;
      const dz = bz - pz;
      if (dx * dx + dy * dy + dz * dz < CORRIDOR_RADIUS * CORRIDOR_RADIUS) {
        rt.world.setAwake(rt.brickBodies[i] as BodyHandle, true);
        rt.retired[i] = 0;
        rt.retireDwell[i] = 0;
      }
    }
  }
}

function brickCenter(rt: SiegeRuntime, i: number): { x: number; y: number; z: number } {
  const o = i * 7;
  const src = rt.fractured[i] ? rt.brickPose : rt.buffer.transforms;
  return {
    x: src[o],
    y: src[o + 1],
    z: src[o + 2],
  };
}

function freezeBrickPose(rt: SiegeRuntime, i: number): void {
  const o = i * 7;
  const tf = rt.buffer.transforms;
  rt.brickPose[o] = tf[o];
  rt.brickPose[o + 1] = tf[o + 1];
  rt.brickPose[o + 2] = tf[o + 2];
  rt.brickPose[o + 3] = tf[o + 3];
  rt.brickPose[o + 4] = tf[o + 4];
  rt.brickPose[o + 5] = tf[o + 5];
  rt.brickPose[o + 6] = tf[o + 6];
}

function evictFragment(rt: SiegeRuntime, body: BodyHandle): void {
  rt.ownership?.unregisterBody(body);
  rt.fragmentBuffer.remove(body);
  rt.fragments.delete(body as number);
  rt.fragmentCount = rt.fragmentPool.bodies.length;
  rt.fragmentWatchDirty = true;
  rt.fragmentGen += 1;
}

function oldestSettledFragment(rt: SiegeRuntime): BodyHandle | null {
  for (const body of rt.fragmentPool.bodies) {
    const rec = rt.fragments.get(body as number);
    if (rec?.settled) return body;
  }
  return null;
}

function ensureFragmentRoom(rt: SiegeRuntime, need: number): boolean {
  if (need <= 0) return false;
  while (rt.fragmentPool.bodies.length + need > MAX_FRAGMENTS) {
    const victim = oldestSettledFragment(rt);
    if (victim == null) return false;
    rt.fragmentPool.retire(victim);
  }
  return rt.fragmentPool.bodies.length + need <= MAX_FRAGMENTS;
}

function hideFracturedBrick(rt: SiegeRuntime, i: number): void {
  freezeBrickPose(rt, i);
  const body = rt.brickBodies[i] as BodyHandle;
  if (rt.caps.setBodyType) rt.world.setBodyType(body, 'static');
  if (rt.caps.setBodyTransform) {
    rt.world.setBodyTransform(body, [0, HIDDEN_BRICK_Y, 0], [0, 0, 0, 1]);
  }
  rt.world.sleepBody(body);
  rt.fractured[i] = 1;
  if (!rt.detached[i]) {
    rt.detached[i] = 1;
    rt.detachedCount += 1;
  }
  if (!rt.retired[i]) {
    rt.retired[i] = 1;
    rt.retiredCount += 1;
  }
}

function addFragmentHull(rt: SiegeRuntime, body: BodyHandle, cell: FractureCell, ordinal: number, sourceBrickOrdinal: number): boolean {
  const density = BRICK_DENSITY / FRAGMENT_HULL_SHRINK ** 3;
  const friction = 1.05;
  const restitution = 0.02;
  if (rt.caps.convexHull) {
    try {
      const points = shrinkHullPoints(cell.vertices, cell.centroid, FRAGMENT_HULL_SHRINK);
      if (points.length >= 4) {
        const shape = rt.world.addHull(body, points, { density, friction, restitution, maxVertices: 32 });
        rt.ownership?.registerShape(shape, body, { kind: 'fragment', ordinal, sourceBrickOrdinal });
        return true;
      }
    } catch {
      // Degenerate hull → box fallback.
    }
  }
  const hx = Math.max(0.012, (cell.aabb.max.x - cell.aabb.min.x) * 0.5 * FRAGMENT_HULL_SHRINK);
  const hy = Math.max(0.008, (cell.aabb.max.y - cell.aabb.min.y) * 0.5 * FRAGMENT_HULL_SHRINK);
  const hz = Math.max(0.008, (cell.aabb.max.z - cell.aabb.min.z) * 0.5 * FRAGMENT_HULL_SHRINK);
  try {
    const shape = rt.world.addBox(body, [hx, hy, hz], { density: BRICK_DENSITY, friction, restitution });
    rt.ownership?.registerShape(shape, body, { kind: 'fragment', ordinal, sourceBrickOrdinal });
    return true;
  } catch {
    return false;
  }
}

function spawnFragmentBody(
  rt: SiegeRuntime,
  cell: FractureCell,
  brickIndex: number,
  seed: number,
  pose: Float32Array,
  poseOffset: number,
  parentV: Vec3,
  parentW: Vec3,
  dir: { x: number; y: number; z: number },
  launch: number,
  spin: number,
): boolean {
  const px = pose[poseOffset];
  const py = pose[poseOffset + 1];
  const pz = pose[poseOffset + 2];
  const qx = pose[poseOffset + 3];
  const qy = pose[poseOffset + 4];
  const qz = pose[poseOffset + 5];
  const qw = pose[poseOffset + 6];
  if (![px, py, pz, qx, qy, qz, qw].every(Number.isFinite)) return false;
  const local = rotateByQuat(cell.centroid.x, cell.centroid.y, cell.centroid.z, qx, qy, qz, qw);
  // Start in front of the wall (+z, toward the cannon) so shards are not
  // interlocked with sleeping neighbours — that contact correction yeets them
  // tens of metres behind the wall, where they land as horizon specks.
  const wx = px + local.x + dir.x * 0.08;
  const wy = py + local.y + 0.03;
  const wz = pz + local.z + dir.z * 0.08 + 0.14;
  let spawned: BodyHandle | null = null;
  const fragmentOrdinal = rt.nextFragmentOrdinal++;
  try {
    spawned = rt.fragmentPool.spawn(() => {
      const body = rt.world.createBody({
        type: 'dynamic',
        position: [wx, wy, wz],
        rotation: [qx, qy, qz, qw],
        ccd: false,
        linearDamping: 0.28,
        angularDamping: 0.35,
      });
      if (rt.ownership) rt.ownership.registerBody(body, { kind: 'fragment', ordinal: fragmentOrdinal, sourceBrickOrdinal: brickIndex });
      if (!addFragmentHull(rt, body, cell, fragmentOrdinal, brickIndex)) {
        rt.ownership?.unregisterBody(body);
        rt.world.destroyBody(body);
        throw new Error('siege fragment hull failed');
      }
      return body;
    }, rt.simTime);
  } catch {
    return false;
  }
  const body = spawned as BodyHandle;
  rt.fragmentBuffer.add(body);
  const rec: SiegeFragment = {
    body,
    cell,
    volume: cell.volume,
    brickIndex,
    seed,
    settled: false,
    bornAt: rt.simTime,
    dwell: 0,
  };
  rt.fragments.set(body as number, rec);
  rt.fragmentsSpawnedTotal += 1;
  rt.fragmentCount = rt.fragmentPool.bodies.length;
  rt.fragmentWatchDirty = true;
  rt.fragmentGen += 1;

  const salt = cell.id + 1;
  const jx = (hash01(seed, 401 + salt) - 0.5) * 1.2;
  const jy = hash01(seed, 409 + salt) * 0.9;
  const jz = (hash01(seed, 419 + salt) - 0.5) * 1.2;
  const rx = cell.centroid.x * 5;
  const ry = cell.centroid.y * 6 + 0.35;
  const rz = cell.centroid.z * 5;
  rt.world.setLinearVelocity(body, [
    parentV.x + dir.x * launch + rx + jx,
    parentV.y + dir.y * launch * 0.4 + ry + jy,
    parentV.z + dir.z * launch + rz + jz,
  ]);
  rt.world.setAngularVelocity(body, [
    parentW.x + (hash01(seed, 431 + salt) - 0.5) * 16 * spin,
    parentW.y + (hash01(seed, 433 + salt) - 0.5) * 14 * spin,
    parentW.z + (hash01(seed, 437 + salt) - 0.5) * 12 * spin,
  ]);
  return true;
}

function fractureBrick(
  rt: SiegeRuntime,
  i: number,
  plan: BrickFracturePlan,
  dir: { x: number; y: number; z: number },
  momentumScale: number,
  strength: number,
): boolean {
  if (plan.keep.length === 0) return false;
  if (!ensureFragmentRoom(rt, plan.keep.length)) return false;
  freezeBrickPose(rt, i);
  const o = i * 7;
  const pose = rt.brickPose;
  const body = rt.brickBodies[i] as BodyHandle;
  const parentV = { x: 0, y: 0, z: 0 };
  const parentW = { x: 0, y: 0, z: 0 };
  if (rt.caps.getLinearVelocity) {
    const v = rt.world.getLinearVelocity(body);
    parentV.x = v.x;
    parentV.y = v.y;
    parentV.z = v.z;
  }
  if (rt.caps.angularVelocity) {
    const w = rt.world.getAngularVelocity(body);
    parentW.x = w.x;
    parentW.y = w.y;
    parentW.z = w.z;
  }
  const brick = rt.ledger.bricks[i];
  const f = detachImpulseFactor(brick.integrity) * strength * momentumScale;
  const launch = Math.min(
    4.4,
    (BASE_DETACH_IMPULSE * f) / Math.max(0.08, BRICK_DENSITY * plan.brickVolume),
  );
  const place = rt.grid.bricks[i];
  let spawned = 0;
  for (const cell of plan.keep) {
    if (rt.fragmentPool.bodies.length >= MAX_FRAGMENTS) break;
    if (spawnFragmentBody(rt, cell, i, place.seed, pose, o, parentV, parentW, dir, launch, f)) {
      spawned += 1;
    }
  }
  if (spawned === 0) return false;
  hideFracturedBrick(rt, i);
  return true;
}

function detachBrick(
  rt: SiegeRuntime,
  i: number,
  dir: { x: number; y: number; z: number },
  momentumScale: number,
  strength: number,
): void {
  const body = rt.brickBodies[i];
  const brick = rt.ledger.bricks[i];
  rt.detached[i] = 1;
  rt.detachedCount += 1;
  rt.retireDwell[i] = 0;
  const f = detachImpulseFactor(brick.integrity) * strength * momentumScale;
  const j = BASE_DETACH_IMPULSE * f;
  const place = rt.grid.bricks[i];
  const jitterA = hash01(place.seed, 71) - 0.5;
  const jitterB = hash01(place.seed, 73) - 0.5;
  const ix = dir.x * j * (0.8 + jitterA * 0.4) + jitterB * j * 0.35;
  const iy = dir.y * j * 0.4 + (jitterA + 0.5) * j * DETACH_UP_BIAS;
  const iz = dir.z * j * (0.8 + jitterB * 0.4) + jitterA * j * 0.35;
  // Apply above center so the escape impulse also spins the brick.
  const c = brickCenter(rt, i);
  rt.world.applyImpulse(body as BodyHandle, [ix, iy, iz], [c.x, c.y + 0.03, c.z]);
  rt.world.setAngularVelocity(body as BodyHandle, [
    (hash01(place.seed, 77) - 0.5) * 14 * f,
    (hash01(place.seed, 79) - 0.5) * 14 * f,
    (hash01(place.seed, 83) - 0.5) * 10 * f,
  ]);
}

/**
 * Fracture coupling — THE deliberate physics hook from docs/damage-system.md.
 * Ledger `removed` ⇒ physical detachment with integrity-scaled impulses; a
 * crumble ring lets pre-damaged rim bricks go on the next hit. High-energy
 * core bricks swap into hull fragments; rim/crumble bricks still fly whole.
 */
function applyFracture(
  rt: SiegeRuntime,
  event: SiegeEvent,
  result: StampResult,
  out: number[],
  fracturedOut: number[],
  pulverizedOut: number[],
): void {
  const momentumScale = Math.min(1.5, event.speed / 48);
  const dir = { x: event.dx, y: event.dy, z: event.dz };
  const budget = fractureBudget(event.energy);
  const core: Array<{ i: number; d: number }> = [];
  const crumble: Array<{ i: number; d: number }> = [];
  const near: Array<{ i: number; d: number }> = [];
  for (let i = 0; i < rt.ledger.bricks.length; i += 1) {
    const brick = rt.ledger.bricks[i];
    if (rt.detached[i] || rt.fractured[i]) continue;
    const place = rt.grid.bricks[i];
    const d = Math.hypot(place.x - event.lx, place.y - event.ly);
    if (brick.state === 'removed') {
      core.push({ i, d });
      continue;
    }
    if (brick.state === 'intact') continue;
    if (d <= result.r1 && brick.integrity < CRUMBLE_INTEGRITY) crumble.push({ i, d });
    else if (d <= result.r2) near.push({ i, d });
  }
  const byDist = (a: { i: number; d: number }, b: { i: number; d: number }) => a.d - b.d || a.i - b.i;
  core.sort(byDist);
  crumble.sort(byDist);
  near.sort(byDist);
  const queue = core.slice();
  const seen = new Set(queue.map((q) => q.i));
  for (const cand of [...crumble, ...near]) {
    if (queue.length >= budget) break;
    if (seen.has(cand.i)) continue;
    seen.add(cand.i);
    queue.push(cand);
  }
  const half = siegeBrickHalf();
  let fractured = 0;
  for (const { i } of queue) {
    let dust = 0;
    if (fractured < budget) {
      const place = rt.grid.bricks[i];
      const plan = planBrickFracture(place.seed, half, event.energy, {
        coreBoost: fractured < 2,
      });
      if (plan.keep.length > 0 && fractureBrick(rt, i, plan, dir, momentumScale, 1)) {
        fractured += 1;
        dust = plan.pulverizedVolume;
        out.push(i);
        fracturedOut.push(i);
        pulverizedOut.push(dust);
        continue;
      }
    }
    detachBrick(rt, i, dir, momentumScale, 1);
    out.push(i);
    pulverizedOut.push(0);
  }
  for (const { i } of crumble) {
    if (rt.detached[i] || rt.fractured[i]) continue;
    detachBrick(rt, i, dir, momentumScale, 0.8);
    out.push(i);
    pulverizedOut.push(0);
  }
}

function pushLastCall(event: SiegeEvent, result: StampResult): LastStampCall {
  return {
    position: [event.lx, event.ly, 0.05],
    direction: [event.dx, event.dy, event.dz],
    energy: event.energy,
    intensity: 0.25,
    blastFraction: 0,
    surfaceNormal: [0, 0, 1],
    profileId: BRICK_PROFILE.id,
    weapon: result.weapon,
    removedDelta: result.removedDelta,
    newPaths: result.newPaths,
  };
}

function handleWallImpact(rt: SiegeRuntime, event: SiegeEvent, ballBody: BodyHandle | null): void {
  const result = rt.ledger.stamp({
    position: [event.lx, event.ly, 0.05],
    direction: [event.dx, event.dy, event.dz],
    energy: event.energy,
    intensity: 0.25,
    blastFraction: 0,
    surfaceNormal: [0, 0, 1],
  });
  rt.stamps.push({
    x: event.lx,
    y: event.ly,
    energy: event.energy,
    blastFraction: 0,
    weapon: 'cannon',
    index: rt.stamps.length + 1,
  });
  rt.residue = buildResidue(rt.ledger, rt.stamps, BRICK_PROFILE);
  const lastCall = pushLastCall(event, result);
  const detached: number[] = [];
  const fractured: number[] = [];
  const pulverizedVolume: number[] = [];
  applyFracture(rt, event, result, detached, fractured, pulverizedVolume);
  rt.lastCall = lastCall;
  if (fractured.length > 0 && ballBody != null && rt.caps.getLinearVelocity) {
    const v = rt.world.getLinearVelocity(ballBody);
    rt.world.setLinearVelocity(ballBody, [v.x * 0.1, v.y * 0.1, v.z * 0.1]);
    if (rt.caps.angularVelocity) rt.world.setAngularVelocity(ballBody, [0, 0, 0]);
  }
  if (rt.pending.length < 16) {
    rt.pending.push({
      kind: 'wall',
      event,
      stamp: result,
      lastCall,
      detached,
      fractured,
      pulverizedVolume,
    });
  }
}

function collectImpacts(rt: SiegeRuntime, suppliedEvents?: readonly { bodyA: BodyHandle; bodyB: BodyHandle; approachSpeed: number }[]): void {
  const events = suppliedEvents ?? rt.world.drainContactBeginEvents();
  for (const event of events) {
    if (event.approachSpeed < MIN_IMPACT_SPEED) continue;
    const ballIdx = rt.cannonballs.indexOf(event.bodyA as BodyHandle);
    const otherIsBall = ballIdx === -1 ? rt.cannonballs.indexOf(event.bodyB as BodyHandle) : -1;
    const ballBody = ballIdx !== -1 ? event.bodyA : otherIsBall !== -1 ? event.bodyB : null;
    if (ballBody == null) continue;
    const other = ballIdx !== -1 ? event.bodyB : event.bodyA;
    const o = rt.buffer.offsetOf(ballBody as BodyHandle);
    if (o == null) continue;
    const tf = rt.buffer.transforms;
    const bx = tf[o];
    const by = tf[o + 1];
    const bz = tf[o + 2];

    const brickIdx = rt.bodyIndex.get(other as number);
    if (brickIdx != null && !rt.detached[brickIdx]) {
      // Contact ≈ ball center pulled back one radius along the shot line.
      const c = brickCenter(rt, brickIdx);
      const dx = bx - c.x;
      const dy = by - c.y;
      const dz = bz - c.z;
      const dl = Math.hypot(dx, dy, dz) || 1;
      const dir = { x: dx / dl, y: dy / dl, z: dz / dl };
      const contact = { x: bx - dir.x * BALL_RADIUS * 0.85, y: by - dir.y * BALL_RADIUS * 0.85, z: bz - dir.z * BALL_RADIUS * 0.85 };
      handleWallImpact(rt, {
        lx: contact.x,
        ly: contact.y,
        wx: contact.x,
        wy: contact.y,
        wz: contact.z,
        dx: dir.x,
        dy: dir.y,
        dz: dir.z,
        energy: siegeHitEnergy(event.approachSpeed, rt.charge),
        speed: event.approachSpeed,
      }, ballBody as BodyHandle);
    } else if (by < BALL_RADIUS * 2.2 && event.approachSpeed >= GROUND_PUFF_SPEED) {
      if (rt.pending.length < 16) {
        rt.pending.push({
          kind: 'ground',
          event: {
            lx: bx,
            ly: 0,
            wx: bx,
            wy: 0.02,
            wz: bz,
            dx: 0,
            dy: 1,
            dz: 0,
            energy: Math.min(1.2, event.approachSpeed / 24),
            speed: event.approachSpeed,
          },
          stamp: null,
          lastCall: null,
          detached: [],
          fractured: [],
          pulverizedVolume: [],
        });
      }
    }
  }
}

const FRAGMENT_SUPPORT_Y = 0.25;
const FRAGMENT_SUPPORT_BELOW = 0.35;
/** Must sit *on* the supporter, not beside a standing wall course. */
const FRAGMENT_SUPPORT_HORIZ = 0.24;

function inYard(x: number, y: number, z: number): boolean {
  if (![x, y, z].every(Number.isFinite)) return false;
  if (y < OOB_CULL_Y) return false;
  if (Math.abs(x) > YARD_MAX_X) return false;
  if (z < YARD_MIN_Z || z > YARD_MAX_Z) return false;
  return true;
}

function poseIsSupported(
  rt: SiegeRuntime,
  pose: { x: number; y: number; z: number },
  skipBody?: BodyHandle | null,
): boolean {
  const { x, y, z } = pose;
  if (!Number.isFinite(y) || y <= FRAGMENT_SUPPORT_Y) return true;
  const r = FRAGMENT_SUPPORT_BELOW;
  const horiz2 = FRAGMENT_SUPPORT_HORIZ * FRAGMENT_SUPPORT_HORIZ;
  for (let i = 0; i < rt.brickBodies.length; i += 1) {
    if (rt.fractured[i]) continue;
    if (skipBody != null && rt.brickBodies[i] === skipBody) continue;
    const c = brickCenter(rt, i);
    if (!(c.y < y - 0.02) || y - c.y > r) continue;
    const dx = c.x - x;
    const dz = c.z - z;
    if (dx * dx + dz * dz <= horiz2) return true;
  }
  const tf = rt.fragmentBuffer.transforms;
  for (const other of rt.fragmentPool.bodies) {
    if (skipBody != null && other === skipBody) continue;
    const oo = rt.fragmentBuffer.offsetOf(other);
    if (oo == null) continue;
    const oy = tf[oo + 1];
    if (!(oy < y - 0.02) || y - oy > r) continue;
    const dx = tf[oo] - x;
    const dz = tf[oo + 2] - z;
    if (dx * dx + dz * dz <= horiz2) return true;
  }
  return false;
}

function fragmentIsSupported(rt: SiegeRuntime, body: BodyHandle): boolean {
  const rec = rt.fragments.get(body as number);
  if (!rec) return false;
  const o = rt.fragmentBuffer.offsetOf(body);
  if (o == null) return false;
  const tf = rt.fragmentBuffer.transforms;
  return poseIsSupported(rt, { x: tf[o], y: tf[o + 1], z: tf[o + 2] }, body);
}

function wakeUnsupportedFragments(rt: SiegeRuntime): void {
  const tf = rt.fragmentBuffer.transforms;
  for (const body of rt.fragmentPool.bodies) {
    const rec = rt.fragments.get(body as number);
    if (!rec) continue;
    const o = rt.fragmentBuffer.offsetOf(body);
    if (o == null) continue;
    const y = tf[o + 1];
    if (!(y > 0.7)) continue;
    if (fragmentIsSupported(rt, body)) continue;
    if (rec.settled) {
      if (rt.caps.setBodyType) rt.world.setBodyType(body, 'dynamic');
      rec.settled = false;
      rec.dwell = 0;
      rt.fragmentWatchDirty = true;
    }
    if (rt.caps.setAwake) rt.world.setAwake(body, true);
  }
}

function wakeUnsupportedDetached(rt: SiegeRuntime, checkIntact: boolean): void {
  if (!rt.caps.setAwake) return;
  for (let i = 0; i < rt.brickBodies.length; i += 1) {
    if (rt.fractured[i] || rt.retired[i]) continue;
    // Sleeping intact bricks never notice support vanishing (slept bodies get
    // no contact-end events), so rows bridge over a fresh breach mid-air and
    // toppled slabs hang where a sweep slept them. Sweep-cadence support scan
    // wakes them; awake intact bricks are already simulating.
    if (!rt.detached[i] && (!checkIntact || rt.sleepStates[i] === 1)) continue;
    const c = brickCenter(rt, i);
    if (poseIsSupported(rt, c, rt.brickBodies[i])) continue;
    rt.world.setAwake(rt.brickBodies[i] as BodyHandle, true);
  }
}

function hideOobBrick(rt: SiegeRuntime, i: number): void {
  const body = rt.brickBodies[i] as BodyHandle;
  if (rt.caps.setBodyType) rt.world.setBodyType(body, 'static');
  if (rt.caps.setBodyTransform) {
    rt.world.setBodyTransform(body, [0, HIDDEN_BRICK_Y, 0], [0, 0, 0, 1]);
  }
  rt.world.sleepBody(body);
  if (!rt.retired[i]) {
    rt.retired[i] = 1;
    rt.retiredCount += 1;
  }
}

function rebuildFragmentWatch(rt: SiegeRuntime): void {
  const live: BodyHandle[] = [];
  for (const body of rt.fragmentPool.bodies) {
    const rec = rt.fragments.get(body as number);
    if (rec && !rec.settled) live.push(body);
  }
  rt.fragmentSleep.watch(live, rt.fragmentBuffer);
  rt.fragmentWatchDirty = false;
}

function retireSettledFragments(rt: SiegeRuntime, dt: number): void {
  const bodies = rt.fragmentPool.bodies.slice();
  for (const body of bodies) {
    const rec = rt.fragments.get(body as number);
    if (!rec) continue;
    const o = rt.fragmentBuffer.offsetOf(body);
    if (o == null) continue;
    const tf = rt.fragmentBuffer.transforms;
    const x = tf[o];
    const y = tf[o + 1];
    const z = tf[o + 2];
    const age = rt.simTime - rec.bornAt;
    if (!inYard(x, y, z) && (rec.settled || y <= FRAGMENT_SUPPORT_Y) && age > 2.5) {
      rt.fragmentPool.retire(body);
      continue;
    }
    if (rec.settled) continue;
    if (!rt.caps.setBodyType) continue;
    let speed = RETIRE_SPEED_EPS + 1;
    let vx = 0;
    let vy = 0;
    let vz = 0;
    if (rt.caps.getLinearVelocity) {
      const v = rt.world.getLinearVelocity(body);
      vx = v.x;
      vy = v.y;
      vz = v.z;
      speed = Math.hypot(vx, vy, vz);
    }
    if (y < 0.2) {
      const horiz = Math.hypot(vx, vz);
      if (horiz > 2.4) {
        const s = 2.4 / horiz;
        rt.world.setLinearVelocity(body, [vx * s, Math.min(vy, 0.5), vz * s]);
        speed = Math.hypot(vx * s, Math.min(vy, 0.5), vz * s);
      }
    }
    if (age > 10 && speed < 0.15) {
      if (y > FRAGMENT_SUPPORT_Y && !fragmentIsSupported(rt, body)) {
        rt.fragmentPool.retire(body);
        continue;
      }
      if (y <= 0.3) {
        rt.world.setBodyType(body, 'static');
        rec.settled = true;
        rt.fragmentWatchDirty = true;
        continue;
      }
    }
    const groundSettle = y <= FRAGMENT_SUPPORT_Y && speed < (age > 4 ? 0.22 : RETIRE_SPEED_EPS);
    if (groundSettle && fragmentIsSupported(rt, body)) {
      rec.dwell += dt;
      if (rec.dwell >= (age > 4 ? 0.45 : RETIRE_DWELL_SEC)) {
        rt.world.setBodyType(body, 'static');
        rec.settled = true;
        rt.fragmentWatchDirty = true;
      }
    } else {
      rec.dwell = 0;
    }
  }
}

function retireSettledDebris(rt: SiegeRuntime, dt: number): void {
  if (!rt.caps.setBodyType) return;
  for (let i = 0; i < rt.brickBodies.length; i += 1) {
    if (rt.retired[i] || rt.fractured[i]) continue;
    const c = brickCenter(rt, i);
    let speed = RETIRE_SPEED_EPS + 1;
    if (rt.caps.getLinearVelocity) {
      const v = rt.world.getLinearVelocity(rt.brickBodies[i] as BodyHandle);
      speed = Math.hypot(v.x, v.y, v.z);
    }
    // Landed / fallen-off-map debris on the infinite ground plane projects
    // as frozen horizon specks. In-flight shards behind the wall must stay.
    if (!inYard(c.x, c.y, c.z) && (c.y < OOB_CULL_Y || (rt.detached[i] && (speed < RETIRE_SPEED_EPS || c.y <= 0.25)))) {
      hideOobBrick(rt, i);
      continue;
    }
    if (!rt.detached[i]) continue;
    if (speed < RETIRE_SPEED_EPS && c.y < SIEGE_BRICK.height * 1.4) {
      rt.retireDwell[i] += dt;
      if (rt.retireDwell[i] >= RETIRE_DWELL_SEC) {
        rt.world.setBodyType(rt.brickBodies[i] as BodyHandle, 'static');
        rt.retired[i] = 1;
        rt.retiredCount += 1;
      }
    } else {
      rt.retireDwell[i] = 0;
    }
  }
}

// resleepQuietIntact was deleted here: sleepBody sleeps the body's whole contact
// island, so re-sleeping one momentarily-slow brick froze entire toppling wall
// slabs mid-air (and the per-frame sleep→contact-wake→split-island churn was the
// interactive slowdown). The cluster-gated rt.sleep.sweep is the only safe
// re-sleeper — it vetoes a cluster when any member still moves.

function readAwake(rt: SiegeRuntime): void {
  if (rt.caps.awakeBodyCount) {
    rt.awakeCount = rt.world.awakeBodyCount();
  }
  if (rt.caps.readSleepStates) {
    const states = rt.sleepStates;
    rt.world.readSleepStates(rt.brickBodyIds, states);
    let awake = 0;
    for (let i = 0; i < states.length; i += 1) {
      if (states[i] === 1 && !rt.retired[i] && !rt.fractured[i]) awake += 1;
    }
    rt.awakeBrickCount = awake;
  }
  const nFrag = rt.fragmentPool.bodies.length;
  rt.fragmentCount = nFrag;
  let awakeFrag = 0;
  if (rt.caps.readSleepStates && nFrag > 0) {
    if (rt.fragmentBodyIds.length < nFrag) rt.fragmentBodyIds = new Int32Array(nFrag);
    if (rt.fragmentSleepStates.length < nFrag) rt.fragmentSleepStates = new Uint8Array(nFrag);
    for (let i = 0; i < nFrag; i += 1) rt.fragmentBodyIds[i] = rt.fragmentPool.bodies[i] as number;
    const ids = rt.fragmentBodyIds.subarray(0, nFrag);
    const states = rt.fragmentSleepStates.subarray(0, nFrag);
    rt.world.readSleepStates(ids, states);
    for (let i = 0; i < nFrag; i += 1) {
      const rec = rt.fragments.get(rt.fragmentPool.bodies[i] as number);
      if (states[i] === 1 && rec && !rec.settled) awakeFrag += 1;
    }
  } else if (rt.caps.bodyQueries) {
    for (const body of rt.fragmentPool.bodies) {
      const rec = rt.fragments.get(body as number);
      if (rec && !rec.settled && rt.world.isBodyAwake(body)) awakeFrag += 1;
    }
  }
  rt.awakeFragmentCount = awakeFrag;
}

/** Advances siege physics; returns the fanout queue for the page to drain. */
export function advanceSiegePhysics(rt: SiegeRuntime, delta: number): SiegeFanout[] {
  const scale = rt.slowMo ? 0.25 : 1;
  let stepMsTotal = 0;
  const stepsThisFrame = rt.stepper.advance(delta * scale, () => {
    const t0 = performance.now();
    rt.world.step(SIEGE_FIXED_DT, SIEGE_SUBSTEPS);
    stepMsTotal += performance.now() - t0;
    rt.simTime += SIEGE_FIXED_DT;
  });
  if (stepsThisFrame) rt.stepMs = stepMsTotal;

  collectImpacts(rt);
  rt.buffer.readInto(rt.world);
  rt.fragmentBuffer.readInto(rt.world);
  const nBricks = rt.brickBodies.length;
  const brickTf = rt.buffer.transforms;
  for (let i = 0; i < nBricks; i += 1) {
    if (rt.fractured[i]) continue;
    const o = i * 7;
    rt.brickPose[o] = brickTf[o];
    rt.brickPose[o + 1] = brickTf[o + 1];
    rt.brickPose[o + 2] = brickTf[o + 2];
    rt.brickPose[o + 3] = brickTf[o + 3];
    rt.brickPose[o + 4] = brickTf[o + 4];
    rt.brickPose[o + 5] = brickTf[o + 5];
    rt.brickPose[o + 6] = brickTf[o + 6];
  }
  if (stepsThisFrame) {
    const simDt = stepsThisFrame * SIEGE_FIXED_DT;
    retireSettledDebris(rt, simDt);
    retireSettledFragments(rt, simDt);
    if (rt.caps.readSleepStates) {
      rt.world.readSleepStates(rt.brickBodyIds, rt.sleepStates);
    }
    if (rt.fragmentPool.sweep(rt.simTime) > 0) rt.fragmentWatchDirty = true;
    if (rt.fragmentWatchDirty) rebuildFragmentWatch(rt);
    rt.sleep.forceSleepSettled();
    rt.sleep.sweep(rt.stepper.simTime);
    rt.fragmentSleep.sweep(rt.stepper.simTime);
    wakeUnsupportedFragments(rt);
    const checkIntact = rt.simTime >= rt.nextHangCheckSim;
    if (checkIntact) rt.nextHangCheckSim = rt.simTime + 2;
    wakeUnsupportedDetached(rt, checkIntact);
    if (rt.prevValid) {
      // prevTransforms kept for potential interpolation; brick range only.
      rt.prevTransforms.set(rt.buffer.transforms.subarray(0, nBricks * 7));
    }
    rt.prevValid = true;
  }
  readAwake(rt);

  const pending = rt.pending;
  if (pending.length) rt.pending = [];
  return pending;
}

/** Hosted fixed-step path: the scene owns stepping and contact reads. */
export function advanceSiegeHostedFixedStep(rt: SiegeRuntime, batch: SiegeContactBatch): void {
  if (!rt.hosted) throw new Error('hosted Siege step requires hosted runtime');
  const t0 = performance.now();
  rt.simTime += SIEGE_FIXED_DT;
  collectImpacts(rt, batch.begins);
  rt.buffer.readInto(rt.world);
  rt.fragmentBuffer.readInto(rt.world);
  const nBricks = rt.brickBodies.length;
  const brickTf = rt.buffer.transforms;
  for (let i = 0; i < nBricks; i += 1) {
    if (rt.fractured[i]) continue;
    const o = i * 7;
    rt.brickPose[o] = brickTf[o]; rt.brickPose[o + 1] = brickTf[o + 1]; rt.brickPose[o + 2] = brickTf[o + 2];
    rt.brickPose[o + 3] = brickTf[o + 3]; rt.brickPose[o + 4] = brickTf[o + 4]; rt.brickPose[o + 5] = brickTf[o + 5]; rt.brickPose[o + 6] = brickTf[o + 6];
  }
  const simDt = SIEGE_FIXED_DT;
  retireSettledDebris(rt, simDt);
  retireSettledFragments(rt, simDt);
  if (rt.caps.readSleepStates) rt.world.readSleepStates(rt.brickBodyIds, rt.sleepStates);
  if (rt.fragmentPool.sweep(rt.simTime) > 0) rt.fragmentWatchDirty = true;
  if (rt.fragmentWatchDirty) rebuildFragmentWatch(rt);
  rt.sleep.forceSleepSettled();
  rt.sleep.sweep(rt.simTime);
  rt.fragmentSleep.sweep(rt.simTime);
  wakeUnsupportedFragments(rt);
  const checkIntact = rt.simTime >= rt.nextHangCheckSim;
  if (checkIntact) rt.nextHangCheckSim = rt.simTime + 2;
  wakeUnsupportedDetached(rt, checkIntact);
  if (rt.prevValid) rt.prevTransforms.set(rt.buffer.transforms.subarray(0, nBricks * 7));
  rt.prevValid = true;
  rt.stepMs = performance.now() - t0;
}

export function finalizeSiegeHostedFrame(rt: SiegeRuntime): SiegeFanout[] {
  if (!rt.hosted) throw new Error('hosted Siege finalize requires hosted runtime');
  readAwake(rt);
  const pending = rt.pending;
  if (pending.length) rt.pending = [];
  return pending;
}

/** Full reset: fresh wall, empty ledger, no balls. */
export function resetSiege(rt: SiegeRuntime): void {
  rt.stepper.reset();
  for (const ball of rt.cannonballs) {
    rt.world.destroyBody(ball as BodyHandle);
    rt.ownership?.unregisterBody(ball as BodyHandle);
    rt.buffer.remove(ball as BodyHandle);
  }
  rt.cannonballs = [];
  rt.fragmentPool.destroyAll();
  rt.fragments.clear();
  rt.fragmentCount = 0;
  rt.fragmentsSpawnedTotal = 0;
  rt.awakeFragmentCount = 0;
  rt.fragmentWatchDirty = true;
  rt.fragmentGen += 1;
  rt.fragmentSleep.watch([], rt.fragmentBuffer);
  for (const body of rt.brickBodies) {
    rt.world.destroyBody(body as BodyHandle);
    rt.ownership?.unregisterBody(body as BodyHandle);
    rt.buffer.remove(body as BodyHandle);
  }
  rt.brickBodies = [];
  rt.bodyIndex.clear();
  rt.detached.fill(0);
  rt.retired.fill(0);
  rt.retireDwell.fill(0);
  rt.fractured.fill(0);
  rt.brickPose.fill(0);
  rt.detachedCount = 0;
  rt.retiredCount = 0;
  rt.awakeCount = 0;
  rt.awakeBrickCount = 0;
  rt.stepMs = 0;
  rt.simTime = 0;
  rt.nextHangCheckSim = 0;
  rt.prevValid = false;
  rt.ledger.reset();
  rt.stamps = [];
  rt.residue = emptyResidue(rt.grid.bricks.length);
  rt.lastCall = null;
  rt.pending = [];
  buildWall(rt);
}

/** Dispose only Siege-owned actors; a hosted world and its shared ground survive. */
export function disposeSiegeStructure(rt: SiegeRuntime): void {
  for (const ball of rt.cannonballs) {
    rt.world.destroyBody(ball);
    rt.ownership?.unregisterBody(ball);
  }
  rt.cannonballs = [];
  rt.fragmentPool.destroyAll();
  rt.fragments.clear();
  for (const body of rt.brickBodies) {
    rt.world.destroyBody(body);
    rt.ownership?.unregisterBody(body);
  }
  rt.brickBodies = [];
  rt.bodyIndex.clear();
  rt.pending = [];
}
